import type {
	ScoreCategory,
	ScoreModuleStatus,
	TreamsScore,
	TreamsScoreRow,
} from './TreamsScore.types';
import { toDisplayDelta, toDisplayScore } from './scoreDisplay';

export type ScoreInsightsInput = {
	selection: string;
	comparisonAvailable: boolean;
	total: { score: number; delta: number; band: string };
	factors: {
		category: ScoreCategory;
		score: number;
		delta: number;
		weight: number;
		state: string;
	}[];
	trend: number[];
};

// Both the quarter and the year chart end today, so their last three monthly
// points are identical and the insight does not change with the interval.
const TREND_POINTS = 3;

export const buildScoreInsightsInput = ({
	selection,
	band,
	scores,
	moduleStatus,
	weights,
	trend,
	comparisonAvailable = true,
}: {
	selection: string;
	band: string;
	scores: TreamsScore;
	moduleStatus: ScoreModuleStatus;
	weights: Record<ScoreCategory, number>;
	trend: number[];
	comparisonAvailable?: boolean;
}): ScoreInsightsInput => ({
	selection,
	comparisonAvailable,
	total: {
		score: toDisplayScore(scores.total.score),
		delta: comparisonAvailable ? toDisplayDelta(scores.total.score, scores.total.delta) : 0,
		band,
	},
	factors: (Object.keys(weights) as ScoreCategory[]).map((category) => ({
		category,
		score: toDisplayScore(scores[category].score),
		delta: comparisonAvailable ? toDisplayDelta(scores[category].score, scores[category].delta) : 0,
		weight: weights[category],
		state: moduleStatus[category],
	})),
	trend: trend.slice(-TREND_POINTS),
});

export const scoreInsightsCacheKey = (rows: Pick<TreamsScoreRow, 'id'>[]): string =>
	rows
		.map((row) => row.id)
		.sort()
		.join('|');

export const countNumberedItems = (markdown: string): number =>
	markdown.match(/^\s*\d+\.\s/gm)?.length ?? 0;
